import { useCallback, useState, type FC } from 'react';

import { Box, Button, Grid, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router';

import AboutHuFi from '@/components/AboutHuFi';
import AutojoinInfoWidget from '@/components/AutojoinInfoWidget';
import CampaignsEmptyState from '@/components/CampaignsEmptyState';
import CampaignsFeed from '@/components/CampaignsFeed';
import CampaignsViewToggle from '@/components/CampaignsViewToggle';
import DashboardWidgets from '@/components/DashboardWidgets';
import FAQ from '@/components/FAQ';
import { useReserveLayoutBottomOffset } from '@/components/Layout';
import MobileBottomNav from '@/components/MobileBottomNav';
import PageErrorState from '@/components/PageErrorState';
import PageWrapper from '@/components/PageWrapper';
import { PERSISTED_CAMPAIGNS_VIEW_KEY, ROUTES } from '@/constants';
import { useIsMobile } from '@/hooks/useBreakpoints';
import { useCampaigns } from '@/hooks/useCampaigns';
import usePagination from '@/hooks/usePagination';
import { ChevronIcon } from '@/icons';
import { useAuthedUserData } from '@/providers/AuthedUserData';
import { useNetwork } from '@/providers/NetworkProvider';
import { type CampaignsQueryParams, CampaignStatus } from '@/types';
import { filterFalsyQueryParams } from '@/utils';

const LinkToCampaigns = () => (
  <Link
    component={RouterLink}
    to={ROUTES.CAMPAIGNS}
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: { xs: 'space-between', sm: 'flex-start' },
      width: { xs: '100%', sm: 'auto' },
      color: 'neutral.100',
      fontSize: { xs: 20, sm: 24 },
      fontWeight: { xs: 500, sm: 800 },
      letterSpacing: { xs: '0px', sm: '-0.5px' },
      gap: 1,
      textDecoration: 'none',
      '&:hover': { textDecoration: 'underline' },
    }}
  >
    Campaigns
    <ChevronIcon sx={{ transform: 'rotate(-90deg)', color: 'neutral.100' }} />
  </Link>
);

const Dashboard: FC = () => {
  const [view, setView] = useState<'grid' | 'table'>(() => {
    const persistedView = localStorage.getItem(PERSISTED_CAMPAIGNS_VIEW_KEY);
    return persistedView === 'table' ? 'table' : 'grid';
  });

  const { isJoinedCampaignsLoading } = useAuthedUserData();
  const { appChainId } = useNetwork();
  const isMobile = useIsMobile();
  useReserveLayoutBottomOffset(isMobile);
  const {
    params: { limit, skip },
  } = usePagination();

  const isGridView = view === 'grid';

  const queryParams = filterFalsyQueryParams({
    chain_id: appChainId,
    status: [CampaignStatus.ACTIVE, CampaignStatus.TO_CANCEL],
    limit,
    skip,
  }) as CampaignsQueryParams;

  const {
    data,
    isLoading: isCampaignsLoading,
    isFetching: isCampaignsFetching,
    isError: isCampaignsError,
    refetch,
  } = useCampaigns(queryParams);

  const handleChangeView = (nextView: 'grid' | 'table') => {
    setView(nextView);
    localStorage.setItem(PERSISTED_CAMPAIGNS_VIEW_KEY, nextView);
  };

  const handleRefetch = useCallback(() => {
    void refetch();
  }, [refetch]);

  const isLoading = isCampaignsLoading || isJoinedCampaignsLoading;

  const campaigns = data?.results || [];

  const showEmptyState =
    !isLoading &&
    !isCampaignsFetching &&
    !isCampaignsError &&
    campaigns.length === 0;

  return (
    <PageWrapper>
      <DashboardWidgets />
      <AutojoinInfoWidget />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: { xs: 2, md: 3 },
        }}
      >
        <LinkToCampaigns />
        {!isMobile && (
          <CampaignsViewToggle
            isGridView={isGridView}
            disableFilters={false}
            onViewChange={handleChangeView}
          />
        )}
      </Box>
      {isCampaignsError && (
        <PageErrorState
          description="We couldn't load campaigns right now. This is on our end, please try again in a moment."
          onRefetch={handleRefetch}
        />
      )}
      {showEmptyState && (
        <CampaignsEmptyState view="all" hasActiveFilters={false} />
      )}
      {!isCampaignsError && !showEmptyState && (
        <CampaignsFeed
          data={campaigns}
          isGridView={isGridView}
          isLoading={isLoading}
          isFetching={isCampaignsFetching}
        />
      )}
      {isMobile && (
        <Button
          component={RouterLink}
          to={ROUTES.CAMPAIGNS}
          variant="outlined"
          size="large"
          fullWidth
          sx={{ mt: 2, color: 'neutral.100', borderColor: 'accent.main' }}
        >
          View All
        </Button>
      )}
      <Grid
        container
        component="section"
        spacing={{ xs: 4, md: 3 }}
        sx={{
          mt: { xs: 4, md: 8 },
          minHeight: { xs: 'auto', md: '400px' },
        }}
      >
        <Grid size={{ xs: 12, md: 5 }}>
          <FAQ />
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <AboutHuFi />
        </Grid>
      </Grid>
      <MobileBottomNav isVisible={isMobile} />
    </PageWrapper>
  );
};

export default Dashboard;
